/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useRef } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { RefreshControl, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { settingsStyles as styles } from './settingsScreen.styles';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { ShellScreenLoading } from '../../shared/layout/ShellScreenLoading'
import { useTabWorkspaceLayout } from '../../shared/layout/useContentLayout';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { useBranchSettingsScreen } from './useBranchSettingsScreen';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import type { ReactNode } from 'react';
import type { KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
type Props = {
  children: (vm: ReturnType<typeof useBranchSettingsScreen>, scrollRef: React.RefObject<KeyboardAwareScrollViewRef | null>) => ReactNode;
  /**
   * Web shell: llena el panel (sin max-width / scroll único) para workspaces
   * tipo rail + main (Tienda online).
   */
  fillWorkspace?: boolean;
};

/** Shell compartido: carga, permisos y scroll para hubs de sucursal. */
export function BranchSettingsHubShell({ children, fillWorkspace = false }: Props) {
  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const vm = useBranchSettingsScreen();
  const { refreshing, onRefresh } = useScreenLoadShell('BranchSettingsHub', async (opts) => {
    await vm.load(opts);
  });
  const navigation = useNavigation<any>();
  const { layoutWide } = useTabWorkspaceLayout();

  const ready = Boolean(vm.currentBranchId) && !vm.loading && vm.canView;

  let body: ReactNode;
  if (!vm.currentBranchId) {
    body = (
      <ModuleEmptyState
        title="Seleccioná una sucursal"
        subtitle="Necesitás una sucursal activa para ver estos ajustes."
        primaryAction={{ label: 'Ir al selector', onPress: vm.handleOpenBranchSelector }}
      />
    );
  } else if (vm.loading) {
    body = <ShellScreenLoading label="Cargando…" />;
  } else if (!vm.canView) {
    body = (
      <ModuleEmptyState
        title="Sin permiso"
        subtitle="Pedile al dueño que te habilite «Ajustes de sucursal» en permisos por empleado."
        primaryAction={{
          label: 'Volver',
          onPress: () => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Profile' as never)),
        }}
      />
    );
  } else {
    body = children(vm, scrollRef);
  }

  if (fillWorkspace && layoutWide && ready) {
    return <View style={workspaceStyles.fill}>{body}</View>;
  }

  return (
    <ShellContentFrame style={ticketSurfaces.screen}>
      <ModalAwareScrollView
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={scrollRef as any}
        style={workspaceStyles.scroll}
        contentContainerStyle={workspaceStyles.content}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={ticketColors.gold}
            colors={[ticketColors.gold]}
          />
        }
      >
        {body}
      </ModalAwareScrollView>
    </ShellContentFrame>
  );
}

const workspaceStyles = StyleSheet.create({
  fill: {
    flex: 1,
    backgroundColor: ticketColors.bg,
    minHeight: 0,
  },
  scroll: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xxl,
  },
});
